import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Home, Thermometer, Hammer, Layers, Beef, CheckCircle2, Phone, Star, ShieldCheck, Award, Clock, Sparkles } from 'lucide-react';
import { SERVICES, COMPANY } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { SERVICE_TRANSLATIONS } from '../i18n/translations';
import { Reveal } from '../hooks/useReveal';
import MagneticButton from '../components/MagneticButton';
import AnimatedCounter from '../components/AnimatedCounter';
import SEO, { breadcrumbLd } from '../components/SEO';

const iconMap = { Building2, Home, Thermometer, Hammer, Layers, Beef, Sparkles };

const trustBadges = [
  { icon: Award, val: '20+', label: 'Jahre Erfahrung' },
  { icon: Star, val: '5.0', label: 'Google Bewertung' },
  { icon: ShieldCheck, val: '100%', label: 'Zuverlässig' },
  { icon: Clock, val: 'Fair', label: 'Preis-Leistung' },
];

const ServicesPage = () => {
  const { t, lang } = useT();

  return (
    <div className="bg-white overflow-x-hidden">
      <SEO
        title="Dienstleistungen · Kundenmaurer, Plattenleger, HardRock, Ab Hof"
        description="Kundenmaurer, Plattenleger, HardRock fugenlose Oberflächen und Ab-Hof-Verkauf in Seelisberg und Uri. Persönlich beraten, transparent kalkuliert, mit AA+ Qualität ausgeführt."
        path="/dienstleistungen"
        lang={lang}
        jsonLd={[breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Dienstleistungen', path: '/dienstleistungen' }])]}
      />
      {/* HERO — Blue gradient */}
      <section className="relative pt-40 pb-20 px-4 sm:px-6 lg:px-8 bg-brand-blueDark text-white overflow-hidden bg-grain">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-brand-blueLight/25 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] bg-brand-blue/30 rounded-full blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-6 bg-white/10 border border-white/20 backdrop-blur px-4 py-2 rounded-full">
              <Sparkles className="w-3.5 h-3.5 text-brand-blueSky" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-brand-blueSky font-bold">Ihr AA+ Handwerker</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[1.02] mb-6 tracking-tight">
              Ihre <span className="italic text-brand-blueSky">Vision</span>.
              <br />
              Mein <span className="text-brand-blueSky">Handwerk</span>.
            </h1>
            <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
              Drei Dienstleistungen, ein Anspruch: <strong className="text-white">AA+ Qualität</strong>. Persönlich beraten, transparent kalkuliert, termintreu ausgeführt.
            </p>

            {/* Trust bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-10">
              {trustBadges.map(({ icon: Icon, val, label }) => (
                <div key={label} className="text-center">
                  <Icon className="w-6 h-6 mx-auto mb-2 text-brand-blueSky" />
                  <div className="font-display text-3xl text-white leading-none">
                    <AnimatedCounter value={val} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mt-1">{label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <MagneticButton>
                <a
                  href={`tel:${COMPANY.phoneInternational}`}
                  className="inline-flex items-center justify-center gap-2 bg-white text-brand-blueDark hover:bg-brand-blueSky px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm shadow-2xl transition-colors btn-pulse-blue"
                  data-testid="services-hero-phone"
                >
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                </a>
              </MagneticButton>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/40 hover:border-white text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors"
                data-testid="services-hero-cta"
              >
                Offerte anfragen
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ALTERNATING SERVICE SECTIONS */}
      {SERVICES.map((s, i) => {
        const Icon = iconMap[s.icon] || Building2;
        const st = SERVICE_TRANSLATIONS[s.id]?.[lang] || {};
        const reverse = i % 2 === 1;
        return (
          <section
            key={s.id}
            className={`py-24 md:py-32 px-4 sm:px-6 lg:px-8 ${i % 2 === 0 ? 'bg-white' : 'bg-brand-beige'}`}
            data-testid={`service-block-${s.slug}`}
          >
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              {/* Image */}
              <Reveal direction={reverse ? 'left' : 'right'} className={reverse ? 'lg:order-2' : ''}>
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-br from-brand-blueLight/30 to-brand-blue/20 rounded-md blur-xl opacity-70 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative aspect-[4/5] rounded-md overflow-hidden shadow-2xl shadow-brand-blueDark/20">
                    <img
                      src={s.image}
                      alt={st.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark/50 via-transparent to-transparent"></div>

                    {/* Number badge */}
                    <div className="absolute top-6 right-6 w-16 h-16 rounded-full bg-white text-brand-blueDark flex items-center justify-center font-display text-2xl font-bold shadow-2xl">
                      0{i + 1}
                    </div>

                    {/* Bottom badge */}
                    <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-4 rounded-sm flex items-center gap-3 shadow-xl">
                      <div className="w-12 h-12 rounded-md bg-brand-blue text-white flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-blueLight font-bold">Dienstleistung</div>
                        <div className="font-display text-lg text-brand-blueDark leading-tight">{st.short}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Content */}
              <Reveal direction={reverse ? 'right' : 'left'} delay={150} className={reverse ? 'lg:order-1' : ''}>
                <div>
                  <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">Dienstleistung 0{i + 1}</div>
                  <h2 className="font-display text-4xl md:text-6xl text-brand-blueDark leading-[1.05] mb-6 tracking-tight">
                    {st.title}
                  </h2>
                  <p className="text-brand-ink/80 text-lg leading-relaxed mb-8">{st.description}</p>

                  <ul className="space-y-3 mb-10">
                    {(st.bullets || []).map((b, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-brand-blueMist flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle2 className="w-4 h-4 text-brand-blue" />
                        </div>
                        <span className="text-brand-ink font-medium">{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-3">
                    <MagneticButton>
                      <Link
                        to={`/dienstleistungen/${s.slug}`}
                        className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blueDark text-white px-7 py-4 text-sm font-bold uppercase tracking-wider rounded-sm shadow-xl shadow-brand-blue/25 transition-colors btn-pulse-blue"
                        data-testid={`detail-cta-${s.slug}`}
                      >
                        Details ansehen
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </MagneticButton>
                    <a
                      href={`tel:${COMPANY.phoneInternational}`}
                      className="inline-flex items-center gap-2 bg-white border-2 border-brand-blueDark text-brand-blueDark hover:bg-brand-blueDark hover:text-white px-7 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Jetzt anrufen
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>
        );
      })}

      {/* MID BANNER — Trust conversion */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-blueDark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 25% 50%, #4A85D0 0%, transparent 40%), radial-gradient(circle at 75% 50%, #1E5AA8 0%, transparent 40%)'
        }}></div>

        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <div className="text-brand-blueSky uppercase tracking-[0.3em] text-xs mb-3 font-bold">Warum Frachigers?</div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight mb-4">
                Der Chef persönlich. <span className="italic text-brand-blueSky">Vom ersten Anruf</span> bis zum letzten Handgriff.
              </h2>
              <p className="text-white/80 leading-relaxed">
                Kein Mittelsmann, keine wechselnden Ansprechpartner. Bei mir bekommen Sie 20+ Jahre gebündelte Handwerkskunst – direkt und ehrlich.
              </p>
            </div>
          </Reveal>
          <Reveal direction="left" delay={200}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: ShieldCheck, title: 'Faire Preise', text: 'Transparent, ohne versteckte Kosten' },
                { icon: Clock, title: 'Termintreu', text: 'Pünktlich und zuverlässig' },
                { icon: Star, title: 'AA+ Qualität', text: 'Schweizer Präzision' },
                { icon: Award, title: 'Persönlich', text: 'Der Chef ist Ihr Kontakt' },
              ].map(({ icon: I, title, text }) => (
                <div key={title} className="bg-white/5 border border-white/10 rounded-md p-5 hover:bg-white/10 transition-colors">
                  <I className="w-7 h-7 text-brand-blueSky mb-3" />
                  <div className="font-display text-lg text-white mb-1">{title}</div>
                  <div className="text-xs text-white/70">{text}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-brand-beige relative overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-blueLight/25 rounded-full blur-3xl"></div>
        <Reveal>
          <div className="relative max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-6xl text-brand-blueDark leading-tight tracking-tight mb-6">
              Bereit für Ihr <span className="italic text-brand-blue">Projekt</span>?
            </h2>
            <p className="text-brand-ink/80 text-lg mb-10">Kostenlose Vor-Ort-Besichtigung und individuelle Beratung. Rufen Sie an – ich freue mich auf Ihr Projekt.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <MagneticButton>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-brand-blueDark hover:bg-brand-blue text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm shadow-xl transition-colors btn-pulse-blue"
                  data-testid="services-final-cta"
                >
                  Kostenlose Beratung
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </MagneticButton>
              <MagneticButton>
                <a
                  href={`tel:${COMPANY.phoneInternational}`}
                  className="inline-flex items-center justify-center gap-2 bg-white border-2 border-brand-blueDark text-brand-blueDark hover:bg-brand-blueDark hover:text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                </a>
              </MagneticButton>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default ServicesPage;
